import * as React from "react";

import "./auto-renew.scss";

import {cs}                from "@Common/react/chain-services";
import {fragments}         from "@Common/react/fragments";
import {Invoke}            from "@Common/react/invoke";
import {keyed}             from "@Common/react/keyed";
import {scnv}              from "@Common/react/state-change-nested-value";
import {toState}           from "@Common/react/to-state";
import {UseState}          from "@Common/react/use-state";
import {InformationBadge}  from "@Common/ui-components/heavy/common/design2/information-badge/information-badge";
import {SliderCheckbox}    from "@Common/ui-components/slider-checkbox/slider-checkbox";
import {cnv}               from "@Common/utils/arr-path";
import {arrEquals, unique} from "@Common/utils/collections";
import {chain}             from "@Common/utils/fs";

export const AutoRenew = ({initEnrollment, allowSeries, program, permanentlyDisableSeries, next}) => cs(
    ["enabledList", ({}, next) => UseState({
        getInitValue: () => chain(
            (initEnrollment?.seriesPreferences ?? [])
                .filter((s) => s.auto_renew)
                .map((s) => s.id)
            ,
            (_) => ({
                current: _,
                saved  : _,
            })),
        next,
    })],
    ["disabled", (_, next) => next(!program.programPreferences.allowAutoRenewal)],
    ["calEnabledList", ({enabledList, disabled}, next) => next(
        disabled ? [] : (
            enabledList.value.current.filter((seriesId) => (
                !permanentlyDisableSeries(seriesId) &&
                allowSeries(seriesId) &&
                true
            ))
        )
    )],
    ({enabledList, calEnabledList}, next) => fragments(
        next(),
        chain(
            enabledList.value.current.filter((id) => !calEnabledList.includes(id)),
            (ids) => ids.map((id) => cs(
                keyed(id),
                () => Invoke({
                    action: () => {
                        scnv(enabledList, ["current"], (list) => list.filter((id1) => id1 !== id))
                    },
                })
            )),
        )
    ),
    ({enabledList, calEnabledList, disabled}) => next({

        enrollmentEditor: {
            valid: true,
            $name: "autoRenew",
            prepareSave: (enrollment) => ({
                newValue: cnv(enrollment, ["seriesPreferences"], (sp) => {
                    const activeIds = unique([
                        ...(sp??[]).map((s) => s.id),
                        ...calEnabledList,
                    ]);

                    return activeIds.map((id) => ({
                        id,
                        ...sp?.find((s) => s.id === id),
                        auto_renew: calEnabledList.includes(id),
                    }));
                }),
                onSaved: () => scnv(enabledList, ["saved"], () => calEnabledList),
            }),
            changed: !arrEquals(calEnabledList, enabledList.value.saved),
        },
        renderSeriesAction: ({seriesId, onSelect}) => cs(
            ({}, next) => disabled || permanentlyDisableSeries(seriesId) ? null : next(),
            ["state", (_, next) => next(toState({
                value: !!calEnabledList?.includes(seriesId),
                onChange: (value) => {
                    if (value) {
                        scnv(enabledList, ["current"], (list) => list.includes(seriesId) ? list : [
                            ...list,
                            seriesId,
                        ]);
                    } else {
                        scnv(enabledList, ["current"], (list) => list.filter((s1) => s1 !== seriesId));
                    }
                },
            }))],
            ({state}) => (
                <div {...{
                    className: "auto-renew-5jd",
                    onClick: () => {
                        scnv(state, [], v=>!v);
                        !state.value && onSelect();
                    },
                }}>
                    <div className="label">
                        Auto Renew editions{" "}
                        {InformationBadge({
                            getTooltip: () => "Choosing this option will automatically repeat the same order pattern for the next cycle of this series.",
                            direction: "south-west",
                            xdQaName: "Auto re-renew tooltip"
                        })}
                    </div>

                    {SliderCheckbox({
                        value: state.value,
                        xdQaName: "Auto Renew editions",
                    })}
                </div>
            ),
        ),
    }),
);
